import { useState, useEffect } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { TamaguiProvider } from 'tamagui';

import LandingPage from './pages/LandingPage';
import AboutPage from './pages/AboutPage';
import ProductPage from './pages/products/ProductPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import ChangelogPage from './pages/ChangelogPage';
import ContactPage from './pages/ContactPage';
import { config } from './tamagui.config';

const THEME_KEY = 'romans-theme';

// Kept in sync with modernDark/modernLight in tamagui.config.ts.
// The mask colour behind the frame, not the sheet inside it.
const GROUND = { dark: '#141010', light: '#e9e2d4' };
const INK = { dark: '#f0ebe4', light: '#241f1b' };

export default function App() {
  const location = useLocation();
  // Light is the default: parchment is the more authentic ground for the identity,
  // and a light-primary site reads as a company rather than a side project — the
  // exact doubt a one-person portfolio has to answer.
  const [theme, setTheme] = useState<'light' | 'dark'>(() => {
    if (typeof window === 'undefined') return 'light';
    try {
      const stored = window.localStorage.getItem(THEME_KEY);
      if (stored === 'light' || stored === 'dark') return stored;
    } catch {
      /* private mode — fall through to the default */
    }
    return 'light';
  });

  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [location.pathname]);

  // Body colours must track the Tamagui grounds exactly. They were pinned to
  // #121215/#e8e8ec, which matched neither theme, so overscroll and the area
  // behind the page showed a colour that appears nowhere in the design.
  const applyBodyTheme = (next: 'light' | 'dark') => {
    document.body.style.backgroundColor = next === 'dark' ? GROUND.dark : GROUND.light;
    document.body.style.color = next === 'dark' ? INK.dark : INK.light;
  };

  useEffect(() => {
    applyBodyTheme(theme);
  }, [theme]);

  const toggleTheme = () => {
    const next = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
    try {
      window.localStorage.setItem(THEME_KEY, next);
    } catch {
      /* private mode — the choice just won't persist */
    }
  };

  return (
    <TamaguiProvider config={config} defaultTheme={theme}>
      <Routes>
        <Route path="/" element={<LandingPage theme={theme} toggleTheme={toggleTheme} />} />
        <Route path="/about" element={<AboutPage theme={theme} toggleTheme={toggleTheme} />} />
        <Route path="/products/:slug" element={<ProductPage theme={theme} toggleTheme={toggleTheme} />} />
        <Route path="/privacy" element={<PrivacyPage theme={theme} toggleTheme={toggleTheme} />} />
        <Route path="/terms" element={<TermsPage theme={theme} toggleTheme={toggleTheme} />} />
        <Route path="/changelog" element={<ChangelogPage theme={theme} toggleTheme={toggleTheme} />} />
        <Route path="/contact" element={<ContactPage theme={theme} toggleTheme={toggleTheme} />} />
      </Routes>
    </TamaguiProvider>
  );
}
